import resolveExpectationField from 'components/DriverAX/utils/resolveExpectationField';

import {
  BonusExclusionMetaData,
  BonusOverrideMetaData,
} from '../../../BonusOverrideIndicator';
import resolveOverridableRewardName, {
  ELIGIBILITY_FIELD,
} from './overridableRewardName';

// The doc's row-level `reasonCode` (recorded once per override change), attached
// to each of its override entries so the tooltip can show it.
const withDocReasonCodes = (
  doc: ElasticDocument | undefined,
  overrides: BonusOverrideMetaData[],
): BonusOverrideMetaData[] => {
  const reasonCodes: string[] | undefined =
    Array.isArray(doc?.['reasonCode']) && doc?.['reasonCode'].length > 0
      ? doc?.['reasonCode']
      : undefined;
  return reasonCodes
    ? overrides.map((o) => ({ ...o, reasonCodes }))
    : overrides;
};

const docOverrides = (
  doc: ElasticDocument | undefined,
): BonusOverrideMetaData[] =>
  Array.isArray(doc?.['_overrides']) ? doc?.['_overrides'] : [];

// Whether an override entry belongs to a fact-table column:
//   - reward overrides → the pay/points column mapping to that reward
//   - the qualifier (eligibility) override → the `qualifiesForBonus` column
//   - criteria overrides → the column for that criteria field (or its target)
const entryMatchesField = ({
  entry,
  field,
  program,
}: {
  entry: BonusOverrideMetaData;
  field: string;
  program: BonusTypes.BonusProgram | undefined;
}): boolean => {
  if (entry.type === 'qualifier') {
    return field === ELIGIBILITY_FIELD;
  }
  if (entry.type === 'reward') {
    const rewardName = resolveOverridableRewardName({ field, program });
    return (
      (!!rewardName && entry.reward === rewardName) || entry.field === field
    );
  }
  const expectation = resolveExpectationField({ program, field });
  return (
    entry.field === field ||
    (!!expectation && entry.field === expectation.actualField)
  );
};

// The dataset field an override entry is shown on when the board has no column
// for it: the reward's pay/points field, the eligibility field, or the criteria
// field. Undefined when it can't be resolved.
const rowFieldForEntry = ({
  entry,
  program,
}: {
  entry: BonusOverrideMetaData;
  program: BonusTypes.BonusProgram | undefined;
}): string | undefined => {
  if (entry.type === 'qualifier') {
    return ELIGIBILITY_FIELD;
  }
  if (entry.type === 'reward') {
    return program?.rewardFields.find((rf) => rf.name === entry.reward)
      ?.rewardValueField;
  }
  return entry.field;
};

/**
 * Places a weekly doc's overrides on rows of the fact table, so each override
 * (and its Clear, in override mode) sits on the row of the item it overrides:
 *   - `extraFields`: fields to add as rows/columns for overrides the board has no
 *     column for (e.g. `serviceBonusPay` for a service override on a Safety board)
 *   - `unplaced`: overrides that can't be tied to any field, plus the doc's
 *     exclusions — shown on the week label instead
 */
export const resolveOverrideRows = ({
  doc,
  fields,
  program,
}: {
  doc: ElasticDocument | undefined;
  fields: string[];
  program: BonusTypes.BonusProgram | undefined;
}): {
  extraFields: string[];
  unplaced: BonusOverrideMetaData[];
  exclusions: BonusExclusionMetaData[];
} => {
  const extraFields: string[] = [];
  const unplaced: BonusOverrideMetaData[] = [];

  docOverrides(doc).forEach((entry) => {
    if (fields.some((field) => entryMatchesField({ entry, field, program }))) {
      return;
    }
    const rowField = rowFieldForEntry({ entry, program });
    if (rowField && entryMatchesField({ entry, field: rowField, program })) {
      if (!extraFields.includes(rowField)) {
        extraFields.push(rowField);
      }
      return;
    }
    unplaced.push(entry);
  });

  return {
    extraFields,
    unplaced: withDocReasonCodes(doc, unplaced),
    exclusions: Array.isArray(doc?.['_exclusions']) ? doc?.['_exclusions'] : [],
  };
};

/**
 * The override entries (from a weekly doc's backend `_overrides` enrichment) that
 * apply to one fact-table column, so the cell can flag them. The doc's row-level
 * `reasonCode` is attached to each entry for the tooltip.
 */
const resolveCellOverrides = ({
  doc,
  field,
  program,
}: {
  doc: ElasticDocument | undefined;
  field: string;
  program: BonusTypes.BonusProgram | undefined;
}): BonusOverrideMetaData[] =>
  withDocReasonCodes(
    doc,
    docOverrides(doc).filter((entry) =>
      entryMatchesField({ entry, field, program }),
    ),
  );

export default resolveCellOverrides;
